import { Component, OnInit, ElementRef, ViewChild, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { Store, select } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { tap, filter, takeUntil, map } from 'rxjs/operators';
import { Subject, Observable, of } from 'rxjs';
import { DateTime } from 'luxon';

import * as fromRootStore from 'src/app/_apps/root/store';
import * as fromAuthStore from 'src/app/_apps/auth/store';
import { Profile } from 'src/app/_apps/auth/models/profile';
import { IAuroraState } from 'src/app/_apps/aurora.state';
import { PopupMenuConfig } from 'src/app/_ui/misc/popup-menu/cog/popup-menu.config';
import { PopupMenuService } from 'src/app/_ui/misc/popup-menu/cog/popup-menu.service';
import { PopupMenuItem } from 'src/app/_ui/misc/popup-menu/cog/popup-menu-item.model';
import { Impersonation } from 'src/app/_apps/root/models';
import { DafaConstants } from 'src/app/_apps/dafa/dafa.constants';

@Component({
    selector: 'aa-header-user-menu',
    templateUrl: './header-user-menu.component.html',
    styleUrls: [
        './header-user-menu.component.scss'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HeaderUserMenuComponent implements OnInit, OnDestroy
{
    private destroyator = new Subject();
    private config: PopupMenuConfig;
    private items: PopupMenuItem[];
    @ViewChild('headerUserMenu') headerUserMenu: ElementRef;
    profileLoaded: boolean = false;
    profile: Profile = new Profile();
    public menuOpened: boolean = false;
    public isImpersonated: Observable<boolean> = of(false);
    public impersonation: Impersonation;
    public isDafaAuthorized: Observable<boolean> = of(false);

    constructor (
        private store: Store<IAuroraState>,
        private actions: Actions,
        private popupMenuService: PopupMenuService
    )
    {
        this.buildPopupMenuItems();
    }

    ngOnInit ()
    {
        this.store
            .pipe(
                select(fromAuthStore.getProfile),
                filter((profile: Profile) => profile !== null),
                tap((profile: Profile) =>
                {
                    this.profileLoaded = true;
                    this.profile = profile;
                }),
                takeUntil(this.destroyator)
            )
            .subscribe();

        this.actions
            .pipe(
                ofType(fromAuthStore.AuthProfileStoreActions.AuthProfileAvatarChange),
                tap((a: { avatarDataUrl: string }) =>
                {
                    this.profile.identiconDataUrl = a.avatarDataUrl;
                }),
                takeUntil(this.destroyator)
            )
            .subscribe();

        this.isImpersonated = this.store
            .pipe(
                select(fromRootStore.getRootImpersonation),
                tap((impersonation: Impersonation) =>
                    this.impersonation = impersonation
                ),
                map((impersonation: Impersonation) =>
                    impersonation !== null
                )
            );
        this.store
            .pipe(
                select(fromAuthStore.getApiAuthorization, { application: DafaConstants.Application, role: null }),
                tap((result: boolean) =>
                    this.items.find((item: PopupMenuItem) => item.id === 'orders').enabled = result
                ),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    ngOnDestroy (): void
    {
        this.profileLoaded = false;
        this.destroyator.next(null);
        this.destroyator.complete();
    }

    openCloseUserMenu ()
    {
        this.popupMenuService.create(this.headerUserMenu, this.config, this.items);
        this.menuOpened = !this.menuOpened;
        // this.store.dispatch(fromRootStore.RootMiscStoreActions.RootShowHideUserMenu({ status: this.menuOpened }));
    }

    private buildPopupMenuItems ()
    {
        this.config = {
            class: 'user-menu',
            positions: [
                {
                    originX: 'start',
                    originY: 'bottom',
                    overlayX: 'start',
                    overlayY: 'top',
                    offsetY: 12,
                    offsetX: -1
                }
            ],
            typePopup: 'popmenu'
        } as PopupMenuConfig;

        const profile: PopupMenuItem = {
            order: 0,
            enabled: true,
            icon: 'fal fa-user-alt',
            id: 'profile',
            key: 'MENU.USER.PROFILE',
            production: DateTime.fromObject({ year: 2019, month: 12, day: 19 }),
            staging: DateTime.fromObject({ year: 2019, month: 12, day: 19 }),
            link: '/personal/profile'
        } as PopupMenuItem;

        const contacts: PopupMenuItem = {
            order: 1,
            enabled: true,
            icon: 'fal fa-address-book fa-flip-horizontal',
            id: 'contacts',
            key: 'MENU.USER.CONTACTS',
            production: DateTime.fromObject({ year: 2019, month: 12, day: 19 }),
            staging: DateTime.fromObject({ year: 2019, month: 12, day: 19 }),
            link: '/personal/contacts'
        } as PopupMenuItem;

        const orders: PopupMenuItem = {
            order: 2,
            enabled: true,
            icon: 'fal fa-file-invoice',
            id: 'orders',
            key: 'MENU.USER.ORDERS',
            production: DateTime.fromObject({ year: 2021, month: 1, day: 1 }),
            staging: DateTime.fromObject({ year: 2020, month: 11, day: 1 }),
            link: '/personal/orders'
        } as PopupMenuItem;

        const contracts: PopupMenuItem = {
            order: 3,
            enabled: true,
            icon: 'fal fa-file-invoice',
            id: 'contracts',
            key: 'MENU.USER.CONTRACTS',
            production: DateTime.fromObject({ year: 2021, month: 1, day: 1 }),
            staging: DateTime.fromObject({ year: 2020, month: 11, day: 1 }),
            link: '/personal/contracts'
        } as PopupMenuItem;

        const entity: PopupMenuItem = {
            order: 4,
            enabled: true,
            icon: 'fal fa-building',
            id: 'entity',
            key: 'MENU.USER.ENTITY',
            production: DateTime.fromObject({ year: 2019, month: 12, day: 19 }),
            staging: DateTime.fromObject({ year: 2019, month: 12, day: 19 }),
            link: '/personal/entity'
        } as PopupMenuItem;

        const logout: PopupMenuItem = {
            order: 10,
            enabled: true,
            icon: 'fal fa-sign-out',
            id: 'logout',
            key: 'MENU.USER.LOGOUT',
            production: DateTime.fromObject({ year: 2019, month: 12, day: 19 }),
            staging: DateTime.fromObject({ year: 2019, month: 12, day: 19 }),
            cta: () =>
            {
                this.store.dispatch(fromAuthStore.AuthLoginStoreActions.AuthLogout());
            },
            separator: true,
            danger: true
        } as PopupMenuItem;

        this.items = [
            profile,
            contacts,
            orders,
            contracts,
            entity,
            logout
        ];
    }
}
